import React, { useRef, useState } from "react";

import { GridColumn } from "@progress/kendo-react-grid";
import { useTranslation } from "react-i18next";

import { Loader, notify, StandardButton } from "@selas/ui-components";
import Endpoint from "../../../../services/api/endpoint";
import { getToken, hasPermission } from "../../../../services/authentication";
import { Permission } from "../../../../utils/enums";

import style from "../../../../assets/editor.module.scss";
import AttachmentEditor from "../../followup/attachment";
import { IAttachment } from "../../../../utils/types/models";
import fileDownload from "js-file-download";
import axios, { AxiosRequestConfig } from "axios";
import { useDispatch } from "react-redux";
import { newKey } from "@selas/utils";
import FileExample from "../../../global/fileExample";
import map from "lodash/map";
import { Dispatch } from "redux";
import { derender, render } from "@selas/state-management";
import GridPanel, { HeaderSelectedState } from "../../../../selas-cloud-platform/gridpanel";

interface IProps {
	projectId: number;
}

const ProjectAttachments: React.FC<IProps> = (props: IProps) => {
	const { t } = useTranslation();
	const [isDownloading, setIsDownloading] = useState(false);
	const selectionRef = useRef<IAttachment[] | HeaderSelectedState>();
	const dispatch: Dispatch = useDispatch();

	function showFileExample(record: IAttachment): void {
		const key: string = newKey("projectAttachment_example");
		dispatch(
			render(key, FileExample, {
				file: record.content,
				onClose: () => dispatch(derender(key))
			})
		);
	}

	function downloadAttachments(): void {
		async function actualDownLoad(): Promise<void> {
			const requestConfig: AxiosRequestConfig = {
				responseType: "blob",
				headers: {
					Authorization: `Bearer ${await getToken()}`
				}
			};
			const body = Array.isArray(selectionRef.current) ? { selectedIds: map(selectionRef.current, "id") } : selectionRef.current ?? { selectedIds: [] };
			const response = await axios.post(`/api/projects/${props.projectId}/attachments/download`, body, requestConfig);
			const contentDisposition: string = response.headers["content-disposition"];
			const match: RegExpMatchArray = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
			const filename: string = match[1];
			fileDownload(response.data, filename);
		}

		if (!selectionRef.current) {
			dispatch(notify(t("info"), t("noAttachmentsSelected"), "info"));
		} else {
			setIsDownloading(true);
			actualDownLoad()
				.then(() => setIsDownloading(false))
				.catch((e) => {
					if (e.response.status === 400) {
						e.response.data.text().then((text: string) => dispatch(notify(t("info"), t(text), "info")));
					} else {
						dispatch(notify(t("info"), t("errorDownloadingAttachments"), "info"));
					}
					setIsDownloading(false);
				});
		}
	}

	function handleSelectionChange(selectionState: IAttachment[] | HeaderSelectedState): void {
		selectionRef.current = selectionState;
	}

	let toolbarButtons: React.ReactElement[];
	if (hasPermission(Permission.ProjectsUpdate)) {
		toolbarButtons = [
			<StandardButton key="downloadAttachments" onClick={downloadAttachments} primary>
				{t("downloadAttachments")}
			</StandardButton>
		];
	}

	return (
		<>
			{isDownloading && <Loader />}
			<GridPanel
				className={style.tabGrid}
				listEndpoint={Endpoint.ProjectAttachmentsList}
				listUrlArguments={{ projectId: props.projectId }}
				endpoint={Endpoint.ProjectAttachments}
				addScreen={{
					screen: AttachmentEditor,
					isAllowed: hasPermission(Permission.ProjectsUpdate),
					extraProps: { projectId: props.projectId }
				}}
				editScreen={{
					screen: AttachmentEditor,
					isAllowed: hasPermission(Permission.ProjectsUpdate),
					extraProps: { projectId: props.projectId }
				}}
				delete={{ urlArguments: { projectId: props.projectId }, isAllowed: hasPermission(Permission.ProjectsUpdate) }}
				extraCommands={[
					{
						tooltip: t("view"),
						iconClass: "las la-search",
						recordAction: showFileExample
					}
				]}
				localLoader
				extraToolbarButtons={toolbarButtons}
				selectionMode="multiple"
				onSelectionChange={handleSelectionChange}
			>
				<GridColumn field="name" title={t("name")} />
			</GridPanel>
		</>
	);
};

export default ProjectAttachments;
